
import React from 'react';

const IndustriesSection: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Industrial Hygiene Services Across Multiple Industries
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              HP Inc Consulting Services offers comprehensive industrial hygiene services tailored to the unique needs of heavy civil construction, transportation infrastructure, structural steel, and specialized industrial operations. Each sector faces specific challenges and risks, requiring specialized approaches to ensure worker health and safety.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Heavy Civil Construction */}
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-road-map-line text-blue-600 text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Heavy Civil Construction</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Industries:</h4>
                  <p className="text-gray-700">Bridge construction, highway projects, tunnels, dams, and large-scale transportation infrastructure.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Services Utilized:</h4>
                  <p className="text-gray-700">Silica monitoring for concrete work, noise monitoring for heavy machinery, air quality assessments for confined spaces and excavations.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Why They Need It:</h4>
                  <p className="text-gray-700">Essential for protecting workers from respiratory hazards, noise-induced hearing loss, and ensuring compliance with OSHA standards for large-scale projects.</p>
                </div>
              </div>
            </div>

            {/* Structural Steel and Fabrication */}
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-building-2-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Structural Steel and Fabrication</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Industries:</h4>
                  <p className="text-gray-700">High-rise construction, industrial facilities, bridge structures, and commercial steel fabrication.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Services Utilized:</h4>
                  <p className="text-gray-700">Welding fume monitoring, hexavalent chromium assessments, noise monitoring for cutting and grinding operations.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Why They Need It:</h4>
                  <p className="text-gray-700">Critical for controlling exposure to carcinogenic welding fumes and protecting workers from respiratory diseases and hearing damage.</p>
                </div>
              </div>
            </div>

            {/* Transportation Infrastructure */}
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-train-line text-green-600 text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Transportation Infrastructure</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Industries:</h4>
                  <p className="text-gray-700">Railway systems, airport construction, port facilities, and mass transit projects.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Services Utilized:</h4>
                  <p className="text-gray-700">Comprehensive air monitoring for diesel exhaust, noise assessments for heavy equipment, and confined space evaluations.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Why They Need It:</h4>
                  <p className="text-gray-700">Ensures worker protection in complex environments with multiple hazards and maintains compliance with federal transportation safety standards.</p>
                </div>
              </div>
            </div>

            {/* Specialized Coating and Painting */}
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-brush-line text-indigo-600 text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Specialized Coating and Painting</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Industries:</h4>
                  <p className="text-gray-700">Bridge painting, industrial coating, automotive refinishing, and infrastructure maintenance.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Services Utilized:</h4>
                  <p className="text-gray-700">Air monitoring for VOCs, lead paint assessments, and respiratory protection evaluations.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Why They Need It:</h4>
                  <p className="text-gray-700">Critical for minimizing exposure to toxic chemicals and ensuring compliance with EPA and OSHA regulations for coating operations.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 bg-blue-50 border border-blue-200 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Specialized Services for Large-Scale Projects
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-building-4-line text-gray-600 mr-3"></i>
                  Infrastructure Development
                </h4>
                <p className="text-gray-700 mb-2">
                  <strong>Projects:</strong> Major highway construction, bridge rehabilitation, tunnel boring, and port expansion
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Services:</strong> Comprehensive exposure assessments, confined space monitoring, and multi-phase project safety evaluations
                </p>
                <p className="text-gray-700">
                  <strong>Purpose:</strong> Ensure worker safety across complex, multi-year infrastructure projects with evolving hazards
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-tools-line text-gray-600 mr-3"></i>
                  Heavy Industrial Operations
                </h4>
                <p className="text-gray-700 mb-2">
                  <strong>Operations:</strong> Steel mills, power plants, petrochemical facilities, and large-scale manufacturing
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Services:</strong> Process-specific monitoring, emergency response planning, and regulatory compliance assessments
                </p>
                <p className="text-gray-700">
                  <strong>Purpose:</strong> Maintain safe operations in high-risk industrial environments with complex regulatory requirements
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-700 mb-8 text-lg leading-relaxed">
              Industrial hygiene services are fundamental to maintaining safe working environments across heavy civil construction, transportation infrastructure, structural steel, and specialized industrial operations. By providing comprehensive monitoring, analysis, and training, we help businesses protect their employees from potential health hazards while ensuring regulatory compliance.
            </p>
            <a 
              href="#contact-form" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer text-lg"
            >
              Get Industry-Specific Assessment
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
